
import React, { useEffect, useRef, useState } from "react";
import HeroContent from "./HeroContent";
import HeroLCPImage from "./HeroLCPImage";
import HeroBackground from "./HeroBackground";
import BackgroundImage from "./BackgroundImage";

// Preload critical images
const preloadImage = (src: string) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
};

// Preload critical images immediately
if (typeof window !== 'undefined') {
  // Desktop hero image
  preloadImage('/lovable-uploads/e9efab5a-5b57-48ca-86e5-4c387ff6423c.png');
  // Mobile hero image  
  preloadImage('/lovable-uploads/b5d6c01f-a0b1-4fe9-81c0-c59db1603f5f.png');
}

const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    // Defer parallax setup to after initial paint for better FCP
    const setupParallax = () => {
      // Disable parallax on mobile/tablet for performance
      if (isMobile || isTablet) return;
      
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrollY = window.scrollY;
            const elements = document.querySelectorAll('.parallax');
            elements.forEach(el => {
              const element = el as HTMLElement;
              const speed = parseFloat(element.dataset.speed || '0.1');
              const yPos = -scrollY * speed;
              element.style.setProperty('--parallax-y', `${yPos}px`);
            });
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    };

    // Defer non-critical parallax setup to improve FCP
    const timeoutId = setTimeout(setupParallax, 0);
    return () => clearTimeout(timeoutId);
  }, [isMobile, isTablet]);

  const getCurrentBackgroundSrc = () => {
    if (isMobile) {
      return "/lovable-uploads/b5d6c01f-a0b1-4fe9-81c0-c59db1603f5f.png";
    } else if (isTablet) {
      return "/lovable-uploads/b5d6c01f-a0b1-4fe9-81c0-c59db1603f5f.png";
    } else {
      return "/lovable-uploads/e9efab5a-5b57-48ca-86e5-4c387ff6423c.png";
    }
  };

  const getBackgroundSize = () => {
    // Fit by width to fill the container
    return 'cover';
  };

  return (
    <BackgroundImage
      src={getCurrentBackgroundSrc()}
      className="overflow-hidden relative bg-cover will-change-transform"
      style={{
        backgroundColor: '#F2F3EE',
        backgroundPosition: isMobile ? 'center top' : 'center center',
        backgroundSize: getBackgroundSize(),
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        padding: isMobile ? '20px 12px 0' : '60px 20px 0',
        minHeight: isMobile ? '600px' : 'auto',
        // Optimize rendering
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
      }}
    >
      <HeroBackground />
      
      <div 
        className={`container px-4 sm:px-6 lg:px-8 h-full flex items-center ${isMobile ? 'pt-8 justify-center' : ''}`}
        ref={containerRef}
      >
        <div className={`flex flex-col lg:flex-row gap-3 lg:gap-12 items-center w-full ${isMobile ? 'justify-center text-center' : ''}`}>
          <div className="flex flex-col">
            <HeroContent isMobile={isMobile} />
          </div>
          <HeroLCPImage isMobile={isMobile} />
        </div>
      </div>
    </BackgroundImage>
  );
};

export default Hero;
